import React, {useMemo} from "react";
import {css} from "@emotion/react";

import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {Image} from "@pg-design/image-module";
import {RequestState} from "@pg-mono/hooks";
import {SkeletonBox} from "@pg-mono/skeleton-box";

import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {GalleryHolderMap} from "./GalleryHolderMap";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");

interface IProps {
    offer: IPropertyDetailOffer | IOfferDetail;
    planImage: string | null | undefined;
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    planImageAltText: string;
    galleryPictures?: IOfferGallery | null;
    galleryFetchState: RequestState;
    propertyGalleryPictures?: IPropertyGallery | null;
    externalPlan?: string | null;
    displayOnlyOneColumn?: boolean;
}

export const GalleryHolderRightSection = (props: IProps) => {
    const {externalPlan, galleryPictures, galleryFetchState, offer, propertyGalleryPictures, planImage, handleGalleryModalChange, planImageAltText} = props;

    const isFirstPropertyGalleryImageAvailable = () => {
        return !!(propertyGalleryPictures && propertyGalleryPictures.length > 0 && propertyGalleryPictures[0]?.image);
    };

    const firstOfferGalleryImage = (
        <Image
            src={galleryPictures?.[0]?.image?.g_img_760x428 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const firstPropertyGalleryImage = (
        <Image
            src={propertyGalleryPictures?.[0]?.image?.g_img_155x87 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const propertyPlanImage = (
        <Image
            src={externalPlan || planImage || ""}
            alt={planImageAltText}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.PLAN);
            }}
            ratio={{
                xs: "760/428"
            }}
            imageStyle={{height: "100%", objectFit: "contain"}}
            css={propertyPlanImageStyles}
        />
    );
    const firstGalleryImage = isFirstPropertyGalleryImageAvailable() ? firstPropertyGalleryImage : firstOfferGalleryImage;
    const galleryImages = galleryPictures?.map((item) => item && item.image && item.image.g_img_372x210).filter(Boolean) as string[];
    const imagesArray = useMemo(() => {
        if (!galleryPictures) {
            return placeholders;
        }
        if (galleryPictures) {
            return galleryImages;
        }
        return [];
    }, [props.offer.id, galleryPictures]);

    const galleryImgSmallArray = galleryPictures ? imagesArray : placeholders;

    const thumbsGalleryHolder = (
        <div css={props.displayOnlyOneColumn ? thumbsGalleryOneColumnHolderStyle : thumbsGalleryHolderStyle}>
            {galleryImgSmallArray.slice(0, props.displayOnlyOneColumn ? 2 : 4).map((item, idx) => {
                return (
                    item && (
                        <div
                            data-gtm-click="Zobacz galerię"
                            key={idx}
                            css={thumbGalleryHolder}
                            onClick={() => {
                                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
                            }}
                        >
                            {galleryFetchState === RequestState.None || galleryFetchState === RequestState.Waiting ? (
                                <div css={skeletonWrapper}>
                                    <SkeletonBox height="100%" width="100%" />
                                </div>
                            ) : (
                                <Image
                                    src={item}
                                    alt={offer.name}
                                    width="375px"
                                    height={undefined}
                                    ratio={{
                                        xs: "375/210"
                                    }}
                                />
                            )}
                        </div>
                    )
                );
            })}
            {!props.displayOnlyOneColumn && (galleryPictures?.length === 2 || galleryPictures?.length === 3) && (
                <div
                    data-gtm-click="Zobacz mapę"
                    css={thumbGalleryHolder}
                    onClick={() => handleGalleryModalChange(OfferModalActiveTab.MAP)}
                    title="Zobacz na mapie"
                >
                    <div css={thumpMapHolder}>
                        <GalleryHolderMap src={offer.map_image} alt={offer.name} />
                    </div>
                </div>
            )}
            {!props.displayOnlyOneColumn && galleryPictures?.length === 2 && <div css={thumbGalleryHolder}>{blankImageSmall}</div>}
        </div>
    );

    const offerRightImage = !galleryPictures || galleryPictures?.length >= 1 ? thumbsGalleryHolder : blankImage;

    return planImage ? propertyPlanImage : isFirstPropertyGalleryImageAvailable() ? firstGalleryImage : offerRightImage;
};

const blankImage = <Image height="760" width={undefined} ratio={{xs: "752/428"}} alt="" />;
const blankImageSmall = <Image height="372" width={undefined} ratio={{xs: "374/210"}} alt="" />;

const placeholder = presaleOfferDefaultImg375x212;
const placeholders = [placeholder, placeholder, placeholder, placeholder, placeholder] as string[];

const thumbsGalleryHolderStyle = css`
    display: grid;
    grid-template: 1fr 1fr / 1fr 1fr;
    column-gap: ${calculateRemSize(2)};
    row-gap: ${calculateRemSize(2)};
`;

const thumbsGalleryOneColumnHolderStyle = css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
`;

const skeletonWrapper = css`
   height: 100%;

    &:after {
        content: "";
        display: block;
        padding-top: 56.25%;
`;
const thumbGalleryHolder = css``;

const thumpMapHolder = css`
    position: relative;
    overflow: hidden;
    line-height: 0;
    aspect-ratio: 374/210;
`;

const propertyPlanImageStyles = css`
    height: 100%;
`;
import React, {useMemo} from "react";
import {css} from "@emotion/react";

import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {Image} from "@pg-design/image-module";
import {RequestState} from "@pg-mono/hooks";
import {SkeletonBox} from "@pg-mono/skeleton-box";

import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {GalleryHolderMap} from "./GalleryHolderMap";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");

interface IProps {
    offer: IPropertyDetailOffer | IOfferDetail;
    planImage: string | null | undefined;
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    planImageAltText: string;
    galleryPictures?: IOfferGallery | null;
    galleryFetchState: RequestState;
    propertyGalleryPictures?: IPropertyGallery | null;
    externalPlan?: string | null;
    displayOnlyOneColumn?: boolean;
}

export const GalleryHolderRightSection = (props: IProps) => {
    const {externalPlan, galleryPictures, galleryFetchState, offer, propertyGalleryPictures, planImage, handleGalleryModalChange, planImageAltText} = props;

    const isFirstPropertyGalleryImageAvailable = () => {
        return !!(propertyGalleryPictures && propertyGalleryPictures.length > 0 && propertyGalleryPictures[0]?.image);
    };

    const firstOfferGalleryImage = (
        <Image
            src={galleryPictures?.[0]?.image?.g_img_760x428 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const firstPropertyGalleryImage = (
        <Image
            src={propertyGalleryPictures?.[0]?.image?.g_img_155x87 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const propertyPlanImage = (
        <Image
            src={externalPlan || planImage || ""}
            alt={planImageAltText}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.PLAN);
            }}
            ratio={{
                xs: "760/428"
            }}
            imageStyle={{height: "100%", objectFit: "contain"}}
            css={propertyPlanImageStyles}
        />
    );
    const firstGalleryImage = isFirstPropertyGalleryImageAvailable() ? firstPropertyGalleryImage : firstOfferGalleryImage;
    const galleryImages = galleryPictures?.map((item) => item && item.image && item.image.g_img_372x210).filter(Boolean) as string[];
    const imagesArray = useMemo(() => {
        if (!galleryPictures) {
            return placeholders;
        }
        if (galleryPictures) {
            return galleryImages;
        }
        return [];
    }, [props.offer.id, galleryPictures]);

    const galleryImgSmallArray = galleryPictures ? imagesArray : placeholders;

    const thumbsGalleryHolder = (
        <div css={props.displayOnlyOneColumn ? thumbsGalleryOneColumnHolderStyle : thumbsGalleryHolderStyle}>
            {galleryImgSmallArray.slice(0, props.displayOnlyOneColumn ? 2 : 4).map((item, idx) => {
                return (
                    item && (
                        <div
                            data-gtm-click="Zobacz galerię"
                            key={idx}
                            css={thumbGalleryHolder}
                            onClick={() => {
                                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
                            }}
                        >
                            {galleryFetchState === RequestState.None || galleryFetchState === RequestState.Waiting ? (
                                <div css={skeletonWrapper}>
                                    <SkeletonBox height="100%" width="100%" />
                                </div>
                            ) : (
                                <Image
                                    src={item}
                                    alt={offer.name}
                                    width="375px"
                                    height={undefined}
                                    ratio={{
                                        xs: "375/210"
                                    }}
                                />
                            )}
                        </div>
                    )
                );
            })}
            {!props.displayOnlyOneColumn && (galleryPictures?.length === 2 || galleryPictures?.length === 3) && (
                <div
                    data-gtm-click="Zobacz mapę"
                    css={thumbGalleryHolder}
                    onClick={() => handleGalleryModalChange(OfferModalActiveTab.MAP)}
                    title="Zobacz na mapie"
                >
                    <div css={thumpMapHolder}>
                        <GalleryHolderMap src={offer.map_image} alt={offer.name} />
                    </div>
                </div>
            )}
            {!props.displayOnlyOneColumn && galleryPictures?.length === 2 && <div css={thumbGalleryHolder}>{blankImageSmall}</div>}
        </div>
    );

    const offerRightImage = !galleryPictures || galleryPictures?.length >= 1 ? thumbsGalleryHolder : blankImage;

    return planImage ? propertyPlanImage : isFirstPropertyGalleryImageAvailable() ? firstGalleryImage : offerRightImage;
};

const blankImage = <Image height="760" width={undefined} ratio={{xs: "752/428"}} alt="" />;
const blankImageSmall = <Image height="372" width={undefined} ratio={{xs: "374/210"}} alt="" />;

const placeholder = presaleOfferDefaultImg375x212;
const placeholders = [placeholder, placeholder, placeholder, placeholder, placeholder] as string[];

const thumbsGalleryHolderStyle = css`
    display: grid;
    grid-template: 1fr 1fr / 1fr 1fr;
    column-gap: ${calculateRemSize(2)};
    row-gap: ${calculateRemSize(2)};
`;

const thumbsGalleryOneColumnHolderStyle = css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
`;

const skeletonWrapper = css`
   height: 100%;

    &:after {
        content: "";
        display: block;
        padding-top: 56.25%;
`;
const thumbGalleryHolder = css``;

const thumpMapHolder = css`
    position: relative;
    overflow: hidden;
    line-height: 0;
    aspect-ratio: 374/210;
`;

const propertyPlanImageStyles = css`
    height: 100%;
`;
